<script lang="ts">
	import type { ActivityType } from '$lib/service/supabase/activity.service';
	import { createEventDispatcher, onMount } from 'svelte';
	import { page } from '$app/stores';
	import type MuxPlayerElement from '@mux/mux-player';
	import { lastWatched } from '../../store/mostRecentLastWatched.store';
	import { currentLessonId } from '$lib/store/currentLesson.store';

	export let title: string | null = null;
	export let id: string | null = null;
	export let playbackId: string | null = null;
	export let cinemaMode: boolean = false;
	export let hasOverlayStart = false;
	export let hasOverlayEnd = false;
	export let hasOverlayPaused = false;
	export let hasFullscreenMode = true;
	export let autoplay = false;
	export let centerControlElements: string = 'none';
	export let hideSeekControls = true;
	export let videoStartTime: number | null = null;

	$: if (videoStartTime && muxPlayer) {
		muxPlayer.currentTime = videoStartTime / 1000;
	}

	const dispatch = createEventDispatcher();
	const heartbeat_interval_ms = 10 * 1000;
	let heartbeat: NodeJS.Timer | null | undefined;

	// These values are bound to properties of the video
	let isPlaying: boolean;
	let isEnded: boolean;
	let videoPlayer: HTMLElement;
	let isFullscreen = false;
	let progress = 0;

	// TODO: Think about implementing the button size properly if it has to be bigger - it's not changing as expected right now :(
	const centerButtonWidth: string = '20px';
	const centerButtonHeight: string = '20px';

	const seekToLastWatchedPosition = async () => {
		if (muxPlayer && $lastWatched) {
			if (isNaN(muxPlayer.duration)) {
				await new Promise((resolve) => setTimeout(resolve, 100));
				seekToLastWatchedPosition();
			} else {
				if ($lastWatched / 1000 < muxPlayer.duration * 0.9) {
					// Seek to last watched position if less than 90% of the video has been watched
					muxPlayer.currentTime = $lastWatched / 1000;
				}
			}
		}
	};

	$: muxPlayer && $lastWatched && seekToLastWatchedPosition();
	$: currentLessonId.set(id);

	export let muxPlayer: MuxPlayerElement | undefined = undefined;

	const handleEvent = (event: any) => {
		isPlaying = !muxPlayer?.paused;
		isEnded = !!muxPlayer?.ended;
		progress = muxPlayer?.currentTime ?? 0;

		switch (event.type) {
			case 'play':
				dispatchActivity('started');
				startHeartbeat();
				break;
			case 'pause':
				dispatchActivity('paused');
				resetHeartbeat();
				break;
			case 'ended':
				isEnded = true;
				dispatchActivity('completed');
				break;
			case 'seeked':
				if (isPlaying) {
					dispatchActivity('seeked_played');
				} else {
					dispatchActivity('seeked_paused');
				}
		}
	};

	$: {
		const height = videoPlayer?.getBoundingClientRect()?.height;
		if (height) {
			dispatch('playerheight', height);
		}
	}

	const startHeartbeat = () => {
		if (!heartbeat) {
			heartbeat = setInterval(() => {
				if (!muxPlayer?.paused) {
					dispatchActivity('heartbeat');
				}
			}, heartbeat_interval_ms);
		}
	};

	const resetHeartbeat = () => {
		if (heartbeat) {
			clearInterval(heartbeat);
		}
		heartbeat = null;
	};

	const dispatchActivity = (activity_type: ActivityType) => {
		const time = muxPlayer?.currentTime;
		if (!time) {
			return;
		}
		dispatch('activity', {
			activity_type,
			currentTime: time,
			heartbeat_interval_ms
		});
	};

	onMount(async () => {
		document.onfullscreenchange = () => {
			isFullscreen = !!document.fullscreenElement;
		};
		await import('@mux/mux-player');
		if (muxPlayer) {
			muxPlayer.autoplay = autoplay;
		}

		return () => resetHeartbeat();
	});
</script>

<svelte:window
	on:resize={() => dispatch('playerheight', videoPlayer.getBoundingClientRect().height)}
/>

<div
	bind:this={videoPlayer}
	class="relative text-white aspect-video{cinemaMode ? ' w-5/6 mx-auto' : ''}"
>
	<mux-player
		bind:this={muxPlayer}
		on:ended={handleEvent}
		on:pause={handleEvent}
		on:play={handleEvent}
		on:progress={handleEvent}
		on:loadeddata={handleEvent}
		on:seeked={handleEvent}
		playback-id={playbackId}
		metadata-video-id={id}
		metadata-video-title={title}
		metadata-viewer-user-id={$page.data.session?.user.id}
		stream-type="on-demand"
		style={hasFullscreenMode || (!isPlaying && hasOverlayStart)
			? `--center-control-elements=${centerControlElements};--center-button-width=${centerButtonWidth}
			  };--center-button-height=${centerButtonHeight}`
			: `--center-control-elements=${centerControlElements};--fullscreen-button: none;--pip-button: none;--playback-rate-button: none;--center-button-width=${centerButtonWidth}
			  };--center-button-height=${centerButtonHeight}`}
		secondary-color={(hasOverlayEnd && isEnded) || (hasOverlayStart && !isPlaying)
			? 'transparent'
			: 'rgba(0 0 0 / 30)'}
		class="aspect-video {hideSeekControls ? 'hide-seek-controls' : ''}"
	/>

	<div class="player-overlay-all {isEnded && hasOverlayEnd ? 'block z-50' : 'hidden'}">
		<!-- USE SLOT FOR OVERLAYS -->
		<slot name="overlay-end" />
	</div>

	<div class="player-overlay-all {progress === 0 && hasOverlayStart ? 'block z-50' : 'hidden'}">
		<!-- USE SLOT FOR OVERLAYS -->
		<slot name="overlay-start" />
	</div>

	<div
		class="player-overlay-all {!isPlaying && progress > 0 && hasOverlayPaused
			? 'block z-10'
			: 'hidden'}"
	>
		<!-- USE SLOT FOR OVERLAYS -->
		<slot name="overlay-paused" />
	</div>

	<!-- USE SLOT FOR OVERLAYS -->
	<slot name="overlay-guard" />
</div>

<style>
	mux-player {
		/* --center-button-width: var(--center-button-width);
		--center-button-height: var(--center-button-height); */
		--center-controls: var(--center-control-elements);
	}

	mux-player.hide-seek-controls {
		--seek-backward-button: none;
		--seek-forward-button: none;
	}

	mux-player::part(bottom play button) {
		z-index: 20;
	}

	/* mux-player.hide-seek-controls::part(center play button) {
		width: var(--center-button-width);
		height: var(--center-button-height);
		display: block !important;
	} */
</style>
